import React, { useContext } from 'react'
import { Alert as MuiAlert, Box, Typography, Switch, Button, Tooltip } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useAlertContext } from '../../contexts/AlertContext'
import EmailIcon from '@mui/icons-material/Email'
import PhoneIcon from '@mui/icons-material/Phone'
import NotificationsIcon from '@mui/icons-material/Notifications'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Pattern } from '../../../builder/contexts/PatternContext'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import LockIcon from '@mui/icons-material/Lock'
import { doc, updateDoc } from 'firebase/firestore'
import { firebase_firestore } from '../../../../common/firebaseConfig'
import { GreenChartTooltip } from '../../../../common/components/GreenChartTooltip'
import { SnackbarHelperContext } from '../../../../contexts/SnackbarHelperContext'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { Alert, useApplicationSuiteContext } from '../../../../contexts/ApplicationSuiteProvider'
import { useModalContext } from '../../../../contexts/ModalProvider'
import { getClientTimeZone } from '../../../../common/timezones'
import { normalizeSchedule, convertScheduleTz } from '../../../../common/backtestSchedule'
import { COMPOSITE_GATE_MESSAGE, compositeGateActive } from '../../../../common/utils/composite'
import TradeParamsReadView from '../TradeParamsReadView'
import SectionTitle from '../../../../layout/settingsMenu/SectionTitle'
import { captureAlertActiveToggled } from '../../../../common/analytics/alertEvents'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

function formatWindowTime(t: string): string {
  if (t === '24:00') return 'End of Day'
  const d = dayjs(t, 'HH:mm')
  return d.isValid() ? d.format('h:mm A') : t
}

function windowLabel(win: { startTime: string; endTime: string }): string {
  if (win.startTime === win.endTime || (win.startTime === '00:00' && win.endTime === '24:00'))
    return 'All day'
  return `${formatWindowTime(win.startTime)} – ${formatWindowTime(win.endTime)}`
}

// SettingsRow component for consistent layout
const SettingsRow: React.FC<{ title: string; data: React.ReactNode }> = ({ title, data }) => {
  return (
    <Box>
      <Typography sx={{ fontSize: '14px', fontWeight: '300' }}>{title}</Typography>
      <Box>
        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: '400',
            paddingLeft: '20px',
            paddingY: '10px',
          }}
        >
          {data}
        </Typography>
      </Box>
    </Box>
  )
}

// Switch row component for toggles with MUI Switch
export const SettingsSwitchRow: React.FC<{
  title?: string
  isActive: boolean
  onChange: (newValue: boolean) => void
  size?: 'small' | 'medium'
  disabled?: boolean
}> = ({ title, isActive, onChange, size = 'medium', disabled }) => {
  return (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      {title && <Typography sx={{ fontWeight: '300' }}>{title}</Typography>}
      <GreenChartTooltip
        title={
          isActive
            ? 'This alert is active, click to deactivate'
            : 'This alert is inactive, click to activate'
        }
        arrow
        placement="left"
      >
        <Switch
          disabled={disabled}
          checked={isActive}
          onChange={(e) => onChange(e.target.checked)}
          color="primary"
          size={size}
          data-tour-id="alert-active-switch"
        />
      </GreenChartTooltip>
    </Box>
  )
}

const SettingsTab: React.FC = () => {
  const theme = useTheme()
  const { canActivateAlerts, canAutotradeAlert, canEnableAutoTrader, setAlertAutoTradeEnabled } =
    useApplicationSuiteContext()
  const { setDefaultAlert, setIsTradingAccountDialogOpen } = useModalContext()
  const { selectedAlert, setSelectedAlert } = useAlertContext()
  const { setSnackbarSettings } = useContext(SnackbarHelperContext)
  const [updatingWebhook, setUpdatingWebhook] = React.useState(false)

  if (!selectedAlert) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography color="text.secondary">No alert selected</Typography>
      </Box>
    )
  }

  const alert = selectedAlert

  const hasAutotraderAccess = canAutotradeAlert(alert)

  // Extract alert settings
  const pattern = alert.pattern as Pattern
  const alertTypes = alert.alertTypes || []
  const symbol = pattern.trigger?.chartDetails?.symbol || 'Unknown'
  const localSchedule = convertScheduleTz(normalizeSchedule(alert), getClientTimeZone())

  // Handler to toggle active state
  const handleToggleActive = async (newValue: boolean) => {
    if (!alert.id || (!alert.active && !canActivateAlerts)) return

    // Composite-bearing patterns can't be evaluated by the alert engine yet
    if (newValue && alert.pattern && compositeGateActive(alert.pattern)) {
      setSnackbarSettings({ severity: 'info', message: COMPOSITE_GATE_MESSAGE })
      return
    }

    try {
      const alertRef = doc(firebase_firestore, 'tradingPlans', alert.id)
      await updateDoc(alertRef, {
        active: newValue,
      })
      captureAlertActiveToggled(alert, newValue, 'settings_tab')

      // Update local state
      setSelectedAlert({
        ...alert,
        active: newValue,
      })

      // Show toast notification
      setSnackbarSettings({
        severity: 'success',
        message: `Alert ${newValue ? 'activated' : 'deactivated'} successfully`,
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
    } catch (error) {
      console.error('Error updating alert active state:', error)

      // Show error toast
      setSnackbarSettings({
        severity: 'error',
        message: `Failed to ${newValue ? 'activate' : 'deactivate'} alert`,
        autoHideDuration: 5000,
        anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
      })
    }
  }

  const updateAlertTradingOptions = () => {
    if (!alert.id) return

    setDefaultAlert(alert)
    setIsTradingAccountDialogOpen(true)
  }

  const isAutotraderActive = alertTypes.includes('webhook')
  const canToggleAutotrader = isAutotraderActive || canEnableAutoTrader(alert)

  const handleAutotradeToggle = async () => {
    if (!alert.id || updatingWebhook) return
    setUpdatingWebhook(true)
    try {
      const enable = !isAutotraderActive
      const result: any = await setAlertAutoTradeEnabled({ alertId: alert.id, enabled: enable })
      if (result?.data?.status === 200) {
        const newAlertTypes: Alert['alertTypes'] = enable
          ? ([...new Set([...alertTypes, 'webhook'])] as Alert['alertTypes'])
          : alertTypes.filter((t) => t !== 'webhook')
        setSelectedAlert({ ...alert, alertTypes: newAlertTypes })
        setSnackbarSettings({
          severity: 'success',
          message: `Auto Trading ${enable ? 'enabled' : 'disabled'}!`,
          autoHideDuration: 6000,
        })
      } else {
        const msg = result?.data?.message || 'Failed to update Auto Trading'
        setSnackbarSettings({ severity: 'error', message: msg, autoHideDuration: 6000 })
      }
    } catch (error) {
      console.error('Error toggling autotrader:', error)
      setSnackbarSettings({
        severity: 'error',
        message: 'Error updating Auto Trading',
        autoHideDuration: 6000,
      })
    } finally {
      setUpdatingWebhook(false)
    }
  }

  // Group schedule windows by day for display
  const scheduleByDay: Record<number, typeof localSchedule.windows> = {}
  localSchedule.windows.forEach((win) => {
    if (!scheduleByDay[win.dayOfWeek]) scheduleByDay[win.dayOfWeek] = []
    scheduleByDay[win.dayOfWeek].push(win)
  })
  const scheduleDisplay = Object.keys(scheduleByDay)
    .map(Number)
    .sort()
    .map((day) => (
      <Box key={day} sx={{ mb: 0.5 }}>
        <Typography variant="body2" fontWeight={600}>
          {DAY_LABELS[day]}
        </Typography>
        {scheduleByDay[day].map((win, i) => (
          <Typography key={i} variant="body2" sx={{ pl: 1, color: 'text.secondary' }}>
            {windowLabel(win)}
          </Typography>
        ))}
      </Box>
    ))

  // Notification method component
  const NotificationMethod = ({
    icon,
    label,
    isActive,
    value,
  }: {
    icon: React.ReactNode
    label: string
    isActive: boolean
    value?: string
  }) => (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box sx={{ position: 'relative', mr: 1 }}>
        {React.cloneElement(icon as React.ReactElement, {
          sx: { color: isActive ? '#ffffff' : '#aaaaaa', fontSize: 20 },
        })}
        {isActive && (
          <CheckCircleIcon
            sx={{
              color: '#4CAF50',
              fontSize: 14,
              position: 'absolute',
              top: -6,
              right: -6,
              backgroundColor: theme.palette.background.custom3 || '#121212',
              borderRadius: '50%',
            }}
          />
        )}
      </Box>
      <Typography
        sx={{
          color: isActive ? '#ffffff' : '#aaaaaa',
          fontSize: '14px',
          mr: 1,
        }}
      >
        {label}
      </Typography>
      {value && (
        <Typography
          sx={{
            color: '#aaaaaa',
            fontSize: '14px',
            maxWidth: '200px',
            wordBreak: 'break-word',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          ({value})
        </Typography>
      )}
    </Box>
  )

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {alert.alertTemplateId && (
        <MuiAlert severity="info" variant="outlined">
          This alert is managed by an admin-controlled template. Its settings are periodically
          synced from that template and any changes made here may be overwritten.
        </MuiAlert>
      )}

      <SettingsSwitchRow
        disabled={!alert.active && !canActivateAlerts}
        title="Active"
        isActive={alert.active || false}
        onChange={handleToggleActive}
      />

      <SettingsRow title="Symbol" data={symbol} />

      <SettingsRow
        title="Schedule"
        data={
          localSchedule.windows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No restriction
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>{scheduleDisplay}</Box>
          )
        }
      />

      <SettingsRow
        title="Notification Method"
        data={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <NotificationMethod icon={<NotificationsIcon />} label="In-app" isActive={true} />
            <NotificationMethod
              icon={<EmailIcon />}
              label="Email"
              value={alert.user?.email}
              isActive={alertTypes.includes('email')}
            />
            <NotificationMethod
              icon={<PhoneIcon />}
              label="Phone"
              value={alert.user?.phoneNumber}
              isActive={alertTypes.includes('sms')}
            />
            {/* Auto Trading - show based on permission (feature flag OR per-alert enabled) */}
            {hasAutotraderAccess ? (
              <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
                <NotificationMethod
                  icon={<AttachMoneyIcon />}
                  label="Auto Trading"
                  isActive={isAutotraderActive}
                />
                <Box display="flex" gap={1}>
                  {alert.trading?.accountId ? (
                    <>
                      <Tooltip
                        title={
                          !canToggleAutotrader
                            ? 'Autotrader limit reached — disable another or upgrade'
                            : ''
                        }
                      >
                        <span>
                          <Button
                            variant={'outlined'}
                            color={isAutotraderActive ? 'error' : 'success'}
                            onClick={handleAutotradeToggle}
                            disabled={updatingWebhook || !canToggleAutotrader}
                            sx={{ height: '30px' }}
                          >
                            {isAutotraderActive ? 'Deactivate' : 'Activate'}
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        variant={'outlined'}
                        color={'primary'}
                        onClick={() => updateAlertTradingOptions()}
                        sx={{ height: '30px' }}
                      >
                        Update
                      </Button>
                    </>
                  ) : (
                    <Button
                      variant={'outlined'}
                      color={'primary'}
                      onClick={() => updateAlertTradingOptions()}
                      sx={{ height: '30px' }}
                    >
                      {alert.trading?.options ? 'Connect Account' : 'Setup'}
                    </Button>
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                display={'flex'}
                alignItems={'center'}
                justifyContent={'space-between'}
                sx={{ opacity: 0.6 }}
              >
                <NotificationMethod
                  icon={<AttachMoneyIcon />}
                  label="Auto Trading"
                  isActive={false}
                />
                <Tooltip title="Upgrade to enable auto trading">
                  <LockIcon color="warning" sx={{ mr: 1 }} />
                </Tooltip>
              </Box>
            )}

            {/* Trade Parameters read-only display */}
            {alert.trading?.options && (
              <Box sx={{ mt: 1 }}>
                <SectionTitle title="Trade Parameters" />
                <TradeParamsReadView alert={alert} />
              </Box>
            )}
          </Box>
        }
      />
    </Box>
  )
}

export default SettingsTab
